/**
 * Coarse human name for a CSS color — "blue", "teal", "orange".
 *
 * Exists for agent legibility, not for design. A map legend encodes meaning as a color swatch
 * ("this teal square means Int Mission"), which is exactly the kind of state a browser agent or a
 * screen reader cannot read: the swatch is a `background-color`, and there is no text anywhere
 * tying the hue to the row. Naming the hue in the accessibility tree closes that loop, so an agent
 * that sees teal geometry on the canvas can find out what teal means.
 *
 * Deliberately coarse. The goal is "which legend row is this?", not color fidelity — twelve hue
 * buckets plus the achromatic cases is enough to disambiguate any legend in the portal, and a
 * finer scale ("cerulean") would be less useful to read aloud, not more.
 */

/** Hue buckets, in degrees, ordered by upper bound. Last entry wraps back to red at 360. */
const HUE_NAMES: ReadonlyArray<readonly [maxDegrees: number, name: string]> = [
	[15, "red"],
	[45, "orange"],
	[65, "yellow"],
	[100, "lime"],
	[150, "green"],
	[175, "teal"],
	[195, "cyan"],
	[240, "blue"],
	// One wide "purple" band rather than indigo/violet/purple. The narrower split put Material's
	// Purple 700 (#7b1fa2, ~282°) and Purple 500 (#9c27b0, ~291°) into *different* names, which is
	// worse than useless — it implies a distinction the eye does not make.
	[275, "indigo"],
	[330, "purple"],
	[345, "pink"],
	[360, "red"],
];

interface Rgb {
	r: number;
	g: number;
	b: number;
}

/** `#abc`, `#aabbcc`, `#aabbccdd`, `rgb(…)` and `rgba(…)`. Returns null for anything else. */
function parseColor(color: string): Rgb | null {
	const value = color.trim().toLowerCase();

	const hexMatch = /^#([0-9a-f]{3,8})$/.exec(value);
	if (hexMatch) {
		const digits = hexMatch[1];
		if (digits.length === 3 || digits.length === 4) {
			return {
				r: parseInt(digits[0] + digits[0], 16),
				g: parseInt(digits[1] + digits[1], 16),
				b: parseInt(digits[2] + digits[2], 16),
			};
		}
		if (digits.length === 6 || digits.length === 8) {
			return {
				r: parseInt(digits.slice(0, 2), 16),
				g: parseInt(digits.slice(2, 4), 16),
				b: parseInt(digits.slice(4, 6), 16),
			};
		}
		return null;
	}

	const rgbMatch = /^rgba?\(\s*([0-9.]+)[\s,]+([0-9.]+)[\s,]+([0-9.]+)/.exec(value);
	if (rgbMatch) {
		const [r, g, b] = [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map(Number);
		if ([r, g, b].some((n) => !Number.isFinite(n))) return null;
		return { r, g, b };
	}

	return null;
}

/** Hue in degrees (0–360), saturation and lightness as 0–1. */
function toHsl({ r, g, b }: Rgb): { hue: number; saturation: number; lightness: number } {
	const [rn, gn, bn] = [r / 255, g / 255, b / 255];
	const max = Math.max(rn, gn, bn);
	const min = Math.min(rn, gn, bn);
	const delta = max - min;
	const lightness = (max + min) / 2;

	if (delta === 0) return { hue: 0, saturation: 0, lightness };

	const saturation = delta / (1 - Math.abs(2 * lightness - 1));

	let hue: number;
	if (max === rn) hue = ((gn - bn) / delta) % 6;
	else if (max === gn) hue = (bn - rn) / delta + 2;
	else hue = (rn - gn) / delta + 4;

	hue *= 60;
	if (hue < 0) hue += 360;

	return { hue, saturation, lightness };
}

/**
 * A one-word color name, or `null` when the input can't be parsed — callers should omit the
 * description entirely rather than announce a guess.
 *
 * Achromatic and near-achromatic inputs resolve to black / white / gray before hue is consulted,
 * because a hue reading on an unsaturated color is noise.
 */
export function colorName(color: string): string | null {
	const rgb = parseColor(color);
	if (rgb === null) return null;

	const { hue, saturation, lightness } = toHsl(rgb);

	if (lightness <= 0.08) return "black";
	if (lightness >= 0.94) return "white";
	if (saturation <= 0.12) return "gray";

	const bucket = HUE_NAMES.find(([maxDegrees]) => hue < maxDegrees);
	const name = bucket ? bucket[1] : "red";

	// "dark red" and "light blue" are worth distinguishing — two legend rows in the same widget
	// routinely differ only in value (e.g. pulled vs planned cores).
	if (lightness <= 0.25) return `dark ${name}`;
	if (lightness >= 0.78) return `light ${name}`;
	return name;
}
